import { useState, useMemo } from "react";
import {
  gameQuestions as mockGameQuestions,
  gameLevels as mockGameLevels,
  gameTitles as mockGameTitles,
  pointRules as mockPointRules,
  gameStats as mockGameStats,
} from "@/mocks/games";
import { useApi } from "@/hooks/useApi";
import { apiSend } from "@/lib/api";
import { confirmDialog } from "@/components/ui/feedback";

type GameTab = "questions" | "levels" | "titles" | "points";

const emptyForm = { title: "", sceneName: "", type: "single", difficulty: "easy", answer: "", options: "", explanation: "" };

function GamesPage() {
  const { data: gameQuestions, reload } = useApi<typeof mockGameQuestions>("/data/gameQuestions", mockGameQuestions);
  const { data: gameLevels } = useApi<typeof mockGameLevels>("/data/gameLevels", mockGameLevels);
  const { data: gameTitles } = useApi<typeof mockGameTitles>("/data/gameTitles", mockGameTitles);
  const { data: pointRules } = useApi<typeof mockPointRules>("/data/pointRules", mockPointRules);
  const { data: gameStats, reload: reloadStats } = useApi<typeof mockGameStats>("/data/gameStats", mockGameStats);

  const [activeTab, setActiveTab] = useState<GameTab>("questions");
  const [searchQuery, setSearchQuery] = useState("");
  const [difficultyFilter, setDifficultyFilter] = useState("all");
  const [selectedQuestion, setSelectedQuestion] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const refresh = () => { reload(); reloadStats(); };
  const createQuestion = async () => {
    if (!form.title.trim()) return;
    await apiSend("POST", "/games/questions", {
      title: form.title, sceneName: form.sceneName, type: form.type, difficulty: form.difficulty,
      answer: form.answer, explanation: form.explanation,
      options: form.options.split("\n").map((s) => s.trim()).filter(Boolean),
      enabled: true,
    });
    setForm(emptyForm); setShowCreate(false); refresh();
  };
  const toggleQuestion = async (id: string) => { await apiSend("POST", `/games/questions/${id}/toggle`); refresh(); };
  const deleteQuestion = async (id: string) => { if (await confirmDialog({ title: "删除题目", message: "确认删除该题目？", confirmText: "删除" })) { await apiSend("DELETE", `/games/questions/${id}`); refresh(); } };

  const filteredQuestions = useMemo(() => {
    return gameQuestions.filter((q) => {
      const matchesSearch = searchQuery === "" || q.title.includes(searchQuery);
      const matchesDifficulty = difficultyFilter === "all" || q.difficulty === difficultyFilter;
      return matchesSearch && matchesDifficulty;
    });
  }, [gameQuestions, searchQuery, difficultyFilter]);

  const difficultyConfig = {
    easy: { label: "简单", color: "bg-emerald-100 text-emerald-700" },
    medium: { label: "中等", color: "bg-amber-100 text-amber-700" },
    hard: { label: "困难", color: "bg-red-100 text-red-700" },
  };

  const typeConfig = {
    single: { label: "单选", color: "bg-blue-100 text-blue-700" },
    judge: { label: "判断", color: "bg-purple-100 text-purple-700" },
    image: { label: "图片", color: "bg-rose-100 text-rose-700" },
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">互动游戏配置</h1>
          <p className="text-sm text-gray-500 mt-1">知识问答、打卡、称号、积分配置</p>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 md:gap-4">
        {[
          { label: "题库总数", value: gameStats.totalQuestions, color: "text-gray-900", bg: "bg-white", border: "border-gray-200" },
          { label: "关卡总数", value: gameStats.totalLevels, color: "text-amber-700", bg: "bg-amber-50", border: "border-amber-200" },
          { label: "称号总数", value: gameStats.totalTitles, color: "text-teal-700", bg: "bg-teal-50", border: "border-teal-200" },
        ].map((stat) => (
          <div key={stat.label} className={`${stat.bg} border ${stat.border} rounded-lg p-4`}>
            <div className="text-xs text-gray-500 mb-1">{stat.label}</div>
            <div className={`text-2xl font-bold ${stat.color}`}>{stat.value}</div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
        <div className="border-b border-gray-200">
          <div className="flex gap-1 px-4 pt-3">
            {[
              { key: "questions" as const, label: "题库", icon: "ri-questionnaire-line" },
              { key: "levels" as const, label: "关卡", icon: "ri-stack-line" },
              { key: "titles" as const, label: "称号", icon: "ri-vip-crown-line" },
              { key: "points" as const, label: "积分规则", icon: "ri-coins-line" },
            ].map((tab) => (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)} className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === tab.key ? "border-teal-600 text-teal-700" : "border-transparent text-gray-500 hover:text-gray-700"}`}>
                <i className={tab.icon} />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="p-4">
          {activeTab === "questions" && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 flex-wrap">
                <div className="relative flex-1 min-w-56">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="搜索题目" className="w-full pl-9 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500" />
                </div>
                <select value={difficultyFilter} onChange={(e) => setDifficultyFilter(e.target.value)} className="px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500">
                  <option value="all">全部难度</option>
                  <option value="easy">简单</option>
                  <option value="medium">中等</option>
                  <option value="hard">困难</option>
                </select>
                <button onClick={() => setShowCreate(true)} className="px-4 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors shadow-sm whitespace-nowrap">
                  <i className="ri-add-line mr-1.5" />新增题目
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">题目</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">关联景点</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">题型</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">难度</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">答案</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">状态</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredQuestions.map((q) => (
                      <tr key={q.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 max-w-xs">
                          <span className="text-sm text-gray-900 truncate block">{q.title}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-gray-700">{q.sceneName}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${typeConfig[q.type].color}`}>
                            {typeConfig[q.type].label}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${difficultyConfig[q.difficulty].color}`}>
                            {difficultyConfig[q.difficulty].label}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm font-medium text-gray-900">{q.answer}</span>
                        </td>
                        <td className="px-4 py-3">
                          <button onClick={() => toggleQuestion(q.id)} className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium cursor-pointer ${q.enabled ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-700"}`}>
                            {q.enabled ? "启用" : "停用"}
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <button onClick={() => deleteQuestion(q.id)} className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors">
                              <i className="ri-delete-bin-line" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === "levels" && (
            <div className="space-y-3">
              {gameLevels.map((level) => (
                <div key={level.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="text-sm font-medium text-gray-900">{level.name}</h3>
                      <p className="text-xs text-gray-500 mt-0.5">{level.unlockCondition} · {level.rewardPoints} 积分</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded transition-colors">
                        <i className="ri-edit-line" />
                      </button>
                      <button className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors">
                        <i className="ri-delete-bin-line" />
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {level.questions.map((qId) => {
                      const q = gameQuestions.find((q) => q.id === qId);
                      return q ? (
                        <span key={qId} className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">{q.title.slice(0, 15)}...</span>
                      ) : null;
                    })}
                  </div>
                </div>
              ))}
              <button className="w-full py-3 border-2 border-dashed border-gray-200 rounded-lg text-sm text-gray-500 hover:border-teal-300 hover:text-teal-600 transition-colors">
                <i className="ri-add-line mr-1" />新增关卡
              </button>
            </div>
          )}

          {activeTab === "titles" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {gameTitles.map((title) => (
                <div key={title.id} className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-amber-100 text-amber-700 rounded-lg mb-3">
                    <i className={`${title.icon} text-xl`} />
                  </div>
                  <h3 className="text-sm font-medium text-gray-900 mb-1">{title.name}</h3>
                  <p className="text-xs text-gray-500 mb-2">{title.description}</p>
                  <p className="text-xs text-gray-400 mb-3">解锁条件：{title.condition}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">{title.unlocked}/{title.total} 已解锁</span>
                    <div className="w-20 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-full bg-amber-500 rounded-full" style={{ width: `${(title.unlocked / title.total) * 100}%` }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === "points" && (
            <div className="space-y-3">
              {pointRules.map((rule, index) => (
                <div key={index} className="flex items-center justify-between py-3 border-b border-gray-100">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-teal-100 text-teal-700 rounded-lg">
                      <i className="ri-coins-line" />
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-900">{rule.action}</span>
                      <p className="text-xs text-gray-500">{rule.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-bold text-teal-700">+{rule.points} 分</span>
                    <button className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded transition-colors">
                      <i className="ri-edit-line" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30" onClick={() => setShowCreate(false)} />
          <div className="relative w-full max-w-lg bg-white rounded-xl shadow-xl p-6 space-y-3">
            <h3 className="text-lg font-semibold text-gray-900">新增题目</h3>
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="题干 *" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
            <div className="grid grid-cols-3 gap-2">
              <input value={form.sceneName} onChange={(e) => setForm({ ...form, sceneName: e.target.value })} placeholder="关联景点" className="px-3 py-2 border border-gray-200 rounded-lg text-sm" />
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="px-3 py-2 border border-gray-200 rounded-lg text-sm">
                <option value="single">单选</option><option value="judge">判断</option><option value="image">图片</option>
              </select>
              <select value={form.difficulty} onChange={(e) => setForm({ ...form, difficulty: e.target.value })} className="px-3 py-2 border border-gray-200 rounded-lg text-sm">
                <option value="easy">简单</option><option value="medium">中等</option><option value="hard">困难</option>
              </select>
            </div>
            <textarea value={form.options} onChange={(e) => setForm({ ...form, options: e.target.value })} placeholder="选项，每行一个（如 A. 175米）" className="w-full h-20 px-3 py-2 border border-gray-200 rounded-lg text-sm resize-none" />
            <input value={form.answer} onChange={(e) => setForm({ ...form, answer: e.target.value })} placeholder="正确答案（如 B）" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
            <textarea value={form.explanation} onChange={(e) => setForm({ ...form, explanation: e.target.value })} placeholder="解析（可选）" className="w-full h-16 px-3 py-2 border border-gray-200 rounded-lg text-sm resize-none" />
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setShowCreate(false)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">取消</button>
              <button onClick={createQuestion} className="px-4 py-2 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700">保存</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default GamesPage;